/**
 * JV Pipeline — Deals Kanban dashboard.
 *
 * Pipeline stages (EN): Issued → Signed → Executed → Completed
 *                       any stage → Failed (terminal unless re-opened).
 *
 * Only when a deal reaches `completed` is the amount posted as income in
 * the Finance module, tagged by `income_nature` (profit/financing/commission/sales).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, X, ChevronRight, AlertTriangle, CheckCircle2, RefreshCw, Trash2,
  FileText, Search, Briefcase, TrendingUp, Building2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: 'issued', label: 'Issued', color: 'bg-gray-100 text-gray-800 border-gray-300', dot: 'bg-gray-400' },
  { key: 'signed', label: 'Signed', color: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500' },
  { key: 'executed', label: 'Executed', color: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500' },
  { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800 border-green-300', dot: 'bg-green-600' },
  { key: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
];

const INCOME_NATURES = [
  { key: 'profit', label: 'Profit', color: 'bg-[#8B5CF6] text-white' },
  { key: 'financing', label: 'Financing', color: 'bg-[#023E8A] text-white' },
  { key: 'commission', label: 'Commission', color: 'bg-[#FF6B35] text-white' },
  { key: 'sales', label: 'Sales', color: 'bg-[#198754] text-white' },
];

const natureOf = (key) => INCOME_NATURES.find((n) => n.key === key) || INCOME_NATURES[1];

const formatMoney = (amount, currency = 'AED') => {
  const n = Number(amount || 0);
  return `${currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const sumByCurrency = (obj) => Object.entries(obj || {}).map(([cur, amt]) => formatMoney(amt, cur)).join(' · ') || '—';

const JVPipelinePage = () => {
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [failDeal, setFailDeal] = useState(null);
  const [search, setSearch] = useState('');
  const [natureFilter, setNatureFilter] = useState('');
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [d, s] = await Promise.all([
        axios.get(`${API_URL}/api/crm/deals`, auth),
        axios.get(`${API_URL}/api/crm/deals/stats/summary`, auth),
      ]);
      setDeals(d.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load pipeline.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    return deals.filter((d) => {
      if (natureFilter && d.income_nature !== natureFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return [d.title, d.counterparty_name, d.deal_number, d.document_reference]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q));
    });
  }, [deals, search, natureFilter]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const d of filtered) (map[d.stage] || map.issued).push(d);
    return map;
  }, [filtered]);

  const advance = async (deal) => {
    try {
      await axios.post(`${API_URL}/api/crm/deals/${deal.id}/advance`, {}, auth);
      toast({ title: 'Advanced', description: `${deal.deal_number} → next stage.` });
      fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Cannot advance', description: e.response?.data?.detail || e.message });
    }
  };

  const reopen = async (deal) => {
    try {
      await axios.post(`${API_URL}/api/crm/deals/${deal.id}/reopen`, {}, auth);
      toast({ title: 'Re-opened', description: `${deal.deal_number} back in pipeline.` });
      fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Cannot reopen', description: e.response?.data?.detail || e.message });
    }
  };

  const remove = async (deal) => {
    if (!window.confirm(`Delete deal ${deal.deal_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/crm/deals/${deal.id}`, auth);
      toast({ title: 'Deleted', description: deal.deal_number });
      fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.response?.data?.detail || e.message });
    }
  };

  // KPI totals
  const expectedTotals = stats
    ? STAGES.slice(0, 4).reduce((acc, s) => {
        const row = stats.by_stage[s.key]?.expected_by_currency || {};
        for (const [cur, amt] of Object.entries(row)) acc[cur] = (acc[cur] || 0) + amt;
        return acc;
      }, {})
    : {};

  const completedTotals = stats?.by_stage?.completed?.executed_by_currency || {};

  return (
    <div className="p-6 space-y-5" data-testid="jv-pipeline-page">
      <BackButton />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#023E8A]" /> JV / Deals Pipeline
          </h1>
          <p className="text-sm text-[#6C757D] mt-1 max-w-2xl">
            Track every commercial deal from Issued to Completed. On <b>Completed</b>, the
            amount is automatically posted as income in Finance, categorised by nature
            (profit / financing / commission / sales).
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#FF6B35] hover:bg-[#E5562D] text-white"
          data-testid="jv-new-deal-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Deal
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="jv-kpi-row">
        <KPI label="Total Pipeline" value={sumByCurrency(expectedTotals)} icon={<TrendingUp className="w-4 h-4" />} color="text-[#023E8A]" />
        {STAGES.map((s) => (
          <KPI
            key={s.key}
            label={s.label}
            value={stats?.by_stage?.[s.key]?.count ?? 0}
            sub={s.key === 'completed' ? sumByCurrency(completedTotals) : null}
            icon={<span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />}
          />
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            placeholder="Search by title, counterparty, deal number, document ref…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="jv-search"
          />
        </div>
        <select
          value={natureFilter}
          onChange={(e) => setNatureFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
          data-testid="jv-nature-filter"
        >
          <option value="">All natures</option>
          {INCOME_NATURES.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
        </select>
      </div>

      {/* Kanban */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-[#6C757D]">Loading pipeline…</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3" data-testid="jv-kanban">
          {STAGES.map((stage) => (
            <div key={stage.key} className="bg-gray-50 rounded-lg border border-gray-200 min-h-[200px]">
              <div className={`flex items-center justify-between px-3 py-2 border-b ${stage.color} rounded-t-lg`}>
                <div className="font-semibold text-sm flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${stage.dot}`} />
                  {stage.label}
                </div>
                <span className="text-xs font-bold">{byStage[stage.key].length}</span>
              </div>
              <div className="p-2 space-y-2">
                {byStage[stage.key].length === 0 ? (
                  <div className="text-xs text-[#9CA3AF] italic p-3 text-center">No deals here</div>
                ) : byStage[stage.key].map((d) => (
                  <DealCard
                    key={d.id}
                    deal={d}
                    onAdvance={() => advance(d)}
                    onFail={() => setFailDeal(d)}
                    onReopen={() => reopen(d)}
                    onEdit={() => setEditDeal(d)}
                    onDelete={() => remove(d)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(createOpen || editDeal) && (
        <DealFormModal
          deal={editDeal}
          onClose={() => { setCreateOpen(false); setEditDeal(null); }}
          onSaved={() => { setCreateOpen(false); setEditDeal(null); fetchAll(); }}
        />
      )}

      {failDeal && (
        <FailModal
          deal={failDeal}
          onClose={() => setFailDeal(null)}
          onDone={() => { setFailDeal(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

// --------------------- KPI ---------------------
const KPI = ({ label, value, sub, icon, color = 'text-[#2B2D42]' }) => (
  <div className="bg-white border rounded-lg px-3 py-2">
    <div className="flex items-center gap-2 text-[10px] text-[#6C757D] uppercase tracking-wide">
      {icon}<span>{label}</span>
    </div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
    {sub && <div className="text-[11px] text-[#6C757D] truncate" title={sub}>{sub}</div>}
  </div>
);

// --------------------- Deal Card ---------------------
const DealCard = ({ deal, onAdvance, onFail, onReopen, onEdit, onDelete }) => {
  const nature = natureOf(deal.income_nature);
  const isFailed = deal.stage === 'failed';
  const isCompleted = deal.stage === 'completed';
  return (
    <div
      className="bg-white border rounded-lg p-2.5 shadow-sm hover:shadow-md transition"
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 flex-wrap">
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${nature.color}`}>{nature.label}</span>
          <code className="text-[10px] text-[#6C757D] bg-gray-100 px-1 rounded">{deal.deal_number}</code>
        </div>
        <button onClick={onEdit} className="text-[#6C757D] hover:text-[#023E8A] text-xs" title="Edit" data-testid={`deal-edit-${deal.id}`}>✎</button>
      </div>
      <div className="text-sm font-semibold text-[#2B2D42] mt-1 line-clamp-2">{deal.title}</div>
      {deal.counterparty_name && (
        <div className="text-xs text-[#6C757D] flex items-center gap-1 mt-0.5">
          <Building2 className="w-3 h-3" />{deal.counterparty_name}
        </div>
      )}
      {deal.document_reference && (
        <div className="text-[10px] text-[#023E8A] mt-0.5 inline-flex items-center gap-1">
          <FileText className="w-3 h-3" /> {deal.document_reference}
        </div>
      )}
      <div className="mt-1.5 text-sm font-bold text-[#023E8A]">
        {formatMoney(isCompleted ? deal.executed_amount : deal.expected_amount, deal.currency)}
      </div>
      {isCompleted && deal.posted_transaction_id && (
        <div className="text-[10px] text-green-700 mt-0.5 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Posted to Finance
        </div>
      )}
      {isFailed && deal.failure_reason && (
        <div className="text-[10px] text-red-700 mt-0.5 italic line-clamp-2">
          <AlertTriangle className="w-3 h-3 inline mr-0.5" />{deal.failure_reason}
        </div>
      )}
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {!isFailed && !isCompleted && (
          <button
            onClick={onAdvance}
            className="flex-1 text-[10px] bg-[#023E8A] hover:bg-[#0353A4] text-white px-2 py-1 rounded inline-flex items-center justify-center gap-1"
            data-testid={`deal-advance-${deal.id}`}
          >
            Advance <ChevronRight className="w-3 h-3" />
          </button>
        )}
        {!isFailed && !isCompleted && (
          <button
            onClick={onFail}
            className="text-[10px] bg-red-50 hover:bg-red-100 text-red-700 px-2 py-1 rounded border border-red-200"
            data-testid={`deal-fail-${deal.id}`}
            title="Mark as failed"
          >
            Fail
          </button>
        )}
        {isFailed && (
          <button
            onClick={onReopen}
            className="flex-1 text-[10px] bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded inline-flex items-center justify-center gap-1"
            data-testid={`deal-reopen-${deal.id}`}
          >
            <RefreshCw className="w-3 h-3" /> Reopen
          </button>
        )}
        {deal.stage === 'issued' && (
          <button
            onClick={onDelete}
            className="text-[10px] text-red-500 hover:text-red-700 p-1"
            data-testid={`deal-delete-${deal.id}`}
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// --------------------- Deal Form Modal ---------------------
const DealFormModal = ({ deal, onClose, onSaved }) => {
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState(() => ({
    title: deal?.title || '',
    company_id: deal?.company_id || '',
    counterparty_id: deal?.counterparty_id || '',
    counterparty_name: deal?.counterparty_name || '',
    generated_document_id: deal?.generated_document_id || '',
    expected_amount: deal?.expected_amount ?? 0,
    executed_amount: deal?.executed_amount ?? '',
    currency: deal?.currency || 'AED',
    income_nature: deal?.income_nature || 'financing',
    notes: deal?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    (async () => {
      try {
        const [c, s, g] = await Promise.all([
          axios.get(`${API_URL}/api/procurement/companies`, auth),
          axios.get(`${API_URL}/api/crm/counterparties`, auth),
          axios.get(`${API_URL}/api/crm/generated-documents/?limit=200`),
        ]);
        setCompanies(c.data || []);
        setSuppliers(s.data || []);
        setDocuments(g.data || []);
      } catch (_) { /* ignore */ }
    })();
  }, []); // eslint-disable-line

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSupplier = (id) => {
    const sup = suppliers.find((x) => x.id === id);
    setForm((p) => ({
      ...p,
      counterparty_id: id,
      counterparty_name: sup ? sup.name : '',
    }));
  };

  const handleDocument = (id) => {
    const doc = documents.find((x) => x.id === id);
    setForm((p) => ({
      ...p,
      generated_document_id: id,
      // Seed commercial fields from the document when creating a new deal.
      ...(doc && !deal ? {
        title: p.title || `Deal for ${doc.template_name}`,
        company_id: p.company_id || doc.company_id || '',
        counterparty_id: p.counterparty_id || doc.counterparty_id || '',
        counterparty_name: p.counterparty_name
          || doc.counterparty_snapshot?.company_name
          || doc.counterparty_snapshot?.name
          || '',
      } : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.expected_amount || form.expected_amount <= 0) {
      toast({ variant: 'destructive', title: 'Missing data', description: 'Title and expected amount are required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        expected_amount: Number(form.expected_amount),
        executed_amount: form.executed_amount === '' ? null : Number(form.executed_amount),
        generated_document_id: form.generated_document_id || null,
        counterparty_id: form.counterparty_id || null,
        company_id: form.company_id || null,
      };
      if (deal) {
        await axios.patch(`${API_URL}/api/crm/deals/${deal.id}`, payload, auth);
        toast({ title: 'Updated', description: deal.deal_number });
      } else {
        const r = await axios.post(`${API_URL}/api/crm/deals`, payload, auth);
        toast({ title: 'Deal created', description: r.data?.deal_number || '' });
      }
      onSaved();
    } catch (e2) {
      toast({ variant: 'destructive', title: 'Save failed', description: e2.response?.data?.detail || e2.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="deal-form-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-5 py-3 bg-gradient-to-r from-[#023E8A] to-[#0353A4] text-white">
          <div className="font-bold flex items-center gap-2"><Briefcase className="w-5 h-5" /> {deal ? `Edit ${deal.deal_number}` : 'New Deal'}</div>
          <button onClick={onClose} data-testid="deal-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="flex-1 overflow-auto p-5 space-y-4">
          <div>
            <label className="text-xs text-[#6C757D]">Title *</label>
            <input required className="w-full px-3 py-2 border rounded text-sm" value={form.title} onChange={(e) => onChange('title', e.target.value)} data-testid="deal-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6C757D]">Income nature *</label>
              <select className="w-full px-3 py-2 border rounded text-sm bg-white" value={form.income_nature} onChange={(e) => onChange('income_nature', e.target.value)} data-testid="deal-nature-select">
                {INCOME_NATURES.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6C757D]">Linked document (optional)</label>
              <select className="w-full px-3 py-2 border rounded text-sm bg-white" value={form.generated_document_id} onChange={(e) => handleDocument(e.target.value)} data-testid="deal-doc-select">
                <option value="">— No linked document —</option>
                {documents.map((d) => (
                  <option key={d.id} value={d.id}>
                    {(d.document_reference || d.template_code || '—')} · {d.template_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6C757D]">Issuer company</label>
              <select className="w-full px-3 py-2 border rounded text-sm bg-white" value={form.company_id} onChange={(e) => onChange('company_id', e.target.value)} data-testid="deal-company-select">
                <option value="">— Select —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.legal_name || c.trading_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#6C757D]">Counterparty (supplier / client / contact)</label>
              <select className="w-full px-3 py-2 border rounded text-sm bg-white" value={form.counterparty_id} onChange={(e) => handleSupplier(e.target.value)} data-testid="deal-supplier-select">
                <option value="">— Select —</option>
                {['supplier', 'client', 'customer', 'contact', 'lead'].map((src) => {
                  const group = suppliers.filter((s) => s.source === src);
                  if (group.length === 0) return null;
                  const label = src === 'supplier' ? 'Suppliers / Partners'
                    : src === 'client' ? 'CRM Clients'
                    : src === 'customer' ? 'Finance Customers'
                    : src === 'contact' ? 'CRM Contacts'
                    : 'CRM Leads';
                  return (
                    <optgroup key={src} label={label}>
                      {group.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#6C757D]">Expected amount *</label>
              <input type="number" min="0" step="0.01" required className="w-full px-3 py-2 border rounded text-sm" value={form.expected_amount} onChange={(e) => onChange('expected_amount', e.target.value)} data-testid="deal-expected-input" />
            </div>
            <div>
              <label className="text-xs text-[#6C757D]">Executed amount (optional)</label>
              <input type="number" min="0" step="0.01" className="w-full px-3 py-2 border rounded text-sm" value={form.executed_amount} onChange={(e) => onChange('executed_amount', e.target.value)} data-testid="deal-executed-input" />
            </div>
            <div>
              <label className="text-xs text-[#6C757D]">Currency</label>
              <select className="w-full px-3 py-2 border rounded text-sm bg-white" value={form.currency} onChange={(e) => onChange('currency', e.target.value)} data-testid="deal-currency-select">
                {['AED', 'USD', 'EUR', 'GBP', 'AOA'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6C757D]">Notes</label>
            <textarea rows={3} className="w-full px-3 py-2 border rounded text-sm" value={form.notes} onChange={(e) => onChange('notes', e.target.value)} data-testid="deal-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="deal-save-btn">
              {saving ? 'Saving…' : (deal ? 'Update deal' : 'Create deal')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --------------------- Fail Modal ---------------------
const FailModal = ({ deal, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/crm/deals/${deal.id}/fail`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Deal marked as failed', description: deal.deal_number });
      onDone();
    } catch (e2) {
      toast({ variant: 'destructive', title: 'Error', description: e2.response?.data?.detail || e2.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="deal-fail-modal">
      <form onSubmit={submit} className="bg-white rounded-lg max-w-md w-full p-5 space-y-4">
        <div className="font-bold text-[#2B2D42] flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" /> Mark deal as failed
        </div>
        <p className="text-sm text-[#6C757D]">
          <code className="bg-gray-100 px-1 rounded">{deal.deal_number}</code> — {deal.title}
        </p>
        <div>
          <label className="text-xs text-[#6C757D]">Reason for failure</label>
          <textarea
            required
            rows={3}
            className="w-full px-3 py-2 border rounded text-sm"
            placeholder="e.g. Counterparty could not provide verifiable SBLC…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="deal-fail-reason"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="deal-fail-confirm">
            {saving ? 'Saving…' : 'Mark as failed'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default JVPipelinePage;
